/**
 * The organisation's document templates. Offered once when a document is
 * first linked (start the response in the house template before writing)
 * and afterwards from the Tender tab. Choosing a template is the primary
 * action: pick one in the list, apply it with the button; skipping is the
 * quiet link under it (testing on 30 Sep found the emphasis inverted).
 * Templates uploaded here go to the organisation's library, the same list
 * the web app renders with.
 */
import { ArrowLeftIcon, CheckIcon, FilePlus2Icon, PaintbrushIcon, UploadIcon } from "@/design/icons";
import { type FC, useEffect, useRef, useState } from "react";

import { Button } from "@/design/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";
import { Skeleton } from "@/design/ui/skeleton";
import { GroupLabel } from "@/design/Row";

import { type AppliedTemplate, loadAppliedTemplate } from "../document/identity";
import { canCreateDocument, createDocumentFromTemplate } from "../document/outline";
import { downloadTemplateBase64, fetchTemplates, uploadTemplate, type DocumentTemplate } from "../tender/api";

export type TemplateSheetProps = {
  open: boolean;
  /** True on the first link of a document, which changes the copy. */
  firstLink?: boolean;
  token: string;
  onClose: () => void;
  /** Ask io.ai to restyle this document with the template (it reads the styles and formats). */
  onApply: (template: DocumentTemplate) => void;
};

export const TemplateSheet: FC<TemplateSheetProps> = ({ open, firstLink, token, onClose, onApply }) => (
  <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {open && <TemplateBody firstLink={firstLink} token={token} onClose={onClose} onApply={onApply} />}
    </SheetContent>
  </Sheet>
);

const TemplateBody: FC<Omit<TemplateSheetProps, "open">> = ({ firstLink, token, onClose, onApply }) => {
  const [templates, setTemplates] = useState<DocumentTemplate[] | null>(null);
  const [applied, setApplied] = useState<AppliedTemplate | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    void loadAppliedTemplate().then(setApplied);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "info" | "error"; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const supported = canCreateDocument();

  const load = async (signal?: AbortSignal) => {
    setError(null);
    try {
      setTemplates(await fetchTemplates(token, signal));
    } catch (err) {
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : "Could not load templates");
      setTemplates([]);
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [token]);

  // Preselect the template already on this document, else the first one, so
  // the primary button has something to apply the moment the list shows.
  useEffect(() => {
    if (!templates?.length) return;
    setSelected((current) => {
      if (current && templates.some((t) => t.file_id === current)) return current;
      const current_applied = applied && templates.find((t) => t.file_id === applied.fileId);
      return (current_applied ?? templates[0]).file_id;
    });
  }, [templates, applied]);

  const start = async (template: DocumentTemplate) => {
    setBusy(template.file_id);
    setNotice(null);
    try {
      await createDocumentFromTemplate(await downloadTemplateBase64(template.file_id, token));
      setNotice({
        tone: "info",
        text: `A new document from ${template.filename} is opening. Link it to this tender when the pane opens there.`,
      });
    } catch (err) {
      setNotice({ tone: "error", text: err instanceof Error ? err.message : "Could not open the template" });
    } finally {
      setBusy(null);
    }
  };

  const upload = async (file: File) => {
    setBusy("upload");
    setNotice(null);
    try {
      await uploadTemplate(file, token);
      await load();
      setNotice({ tone: "info", text: `${file.name} added to your organisation's templates.` });
    } catch (err) {
      setNotice({ tone: "error", text: err instanceof Error ? err.message : "Upload failed" });
    } finally {
      setBusy(null);
    }
  };

  const chosen = templates?.find((t) => t.file_id === selected) ?? null;
  const chosenApplied = Boolean(chosen && applied?.fileId === chosen.file_id);
  const name = (template: DocumentTemplate) => template.filename.replace(/\.docx?$/i, "");

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">Template</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <div className="px-4 pb-2 pt-4">
          <SheetTitle className="text-lg font-semibold leading-snug text-fg">
            {firstLink ? "Start in your template?" : "Document templates"}
          </SheetTitle>
          <SheetDescription className="mt-0.5 text-sm text-fg-3">
            {firstLink
              ? "Pick your organisation's template and io.ai applies its house style, cover and headers to this document."
              : "Pick a template and io.ai restyles this document to match, or open a new document from it."}
          </SheetDescription>
        </div>

        {!supported && (
          <div className="mx-3 mt-1 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs text-fg-2">
            This version of Word can&apos;t open new documents from the pane; applying a template still works.
          </div>
        )}

        {templates === null ? (
          <div className="flex flex-col gap-3 px-3 pt-4">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between pr-2">
              <GroupLabel>{templates.length > 0 ? "Your templates" : "Templates"}</GroupLabel>
              <input
                ref={fileInput}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void upload(file);
                }}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={busy === "upload"}
                title="Add a .docx to your organisation's template library"
                className="flex h-6 items-center gap-1 rounded-sm px-1.5 text-xs text-fg-3 transition-colors hover:bg-subtle hover:text-fg disabled:opacity-40"
              >
                <UploadIcon className="size-3" />
                {busy === "upload" ? "Uploading…" : "Upload .docx"}
              </button>
            </div>
            <div
              role="radiogroup"
              aria-label="Templates"
              className="mx-3 overflow-hidden rounded-md border border-line"
            >
              {templates.map((template, i) => {
                const opening = busy === template.file_id;
                const isSelected = template.file_id === selected;
                return (
                  <div
                    key={template.file_id}
                    className={
                      "flex items-center gap-2 pr-1.5 transition-colors " +
                      (i === 0 ? "" : "border-t border-line/70 ") +
                      (isSelected ? "bg-accent-soft/60" : "hover:bg-subtle")
                    }
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelected(template.file_id)}
                      onDoubleClick={() => onApply(template)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 py-2 pl-3 text-left"
                    >
                      <span
                        aria-hidden
                        className={
                          "flex size-4 shrink-0 items-center justify-center rounded-full border " +
                          (isSelected ? "border-accent bg-accent text-fg-inverse" : "border-line-strong bg-surface")
                        }
                      >
                        {isSelected && <CheckIcon className="size-2.5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{name(template)}</span>
                        <span className="block truncate text-sm text-fg-3">
                          {opening
                            ? "Opening…"
                            : applied?.fileId === template.file_id
                              ? "Applied to this document"
                              : "Organisation template"}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={`New document from ${name(template)}`}
                      disabled={!supported || Boolean(busy)}
                      title="Open a new document from this template"
                      onClick={() => void start(template)}
                      className="flex size-7 shrink-0 items-center justify-center rounded-sm text-fg-3 transition-colors hover:bg-subtle hover:text-fg disabled:opacity-40"
                    >
                      <FilePlus2Icon className="size-3.5" />
                    </button>
                  </div>
                );
              })}
              {templates.length === 0 && !error && (
                <div className="px-3 py-4 text-sm text-fg-3">
                  No templates yet. Upload your organisation&apos;s .docx to start every response in it.
                </div>
              )}
            </div>
            {templates.length > 0 && (
              <p className="px-4 pt-2 text-xs text-fg-4">
                Applying keeps what you have written: io.ai imports the template&apos;s styles, adds its cover and
                closing pages, and copies its header, footer and page setup. The page icon opens a fresh document from
                the template instead.
              </p>
            )}
            {error && <div className="px-4 py-3 text-sm text-danger">{error}</div>}
          </>
        )}

        {notice && (
          <div
            className={
              notice.tone === "error"
                ? "mx-3 mt-3 rounded-sm border border-danger/30 bg-danger-soft px-2.5 py-2 text-xs text-danger"
                : "mx-3 mt-3 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs text-fg-2"
            }
          >
            {notice.text}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-line/70 px-3 pb-2.5 pt-2.5">
        <Button
          className="w-full gap-1.5"
          disabled={!chosen || Boolean(busy)}
          title={chosen ? `Ask io.ai to apply ${name(chosen)}` : "Pick a template first"}
          onClick={() => chosen && onApply(chosen)}
        >
          <PaintbrushIcon className="size-3.5" />
          {chosen ? (chosenApplied ? `Apply ${name(chosen)} again` : `Apply ${name(chosen)}`) : "Apply template"}
        </Button>
        <button
          type="button"
          onClick={onClose}
          className="mt-1.5 w-full py-1 text-center text-sm text-fg-3 hover:text-fg"
        >
          {firstLink ? "Continue without a template" : "Close"}
        </button>
      </div>
    </div>
  );
};
